/**
 * SSOT for same-kind verify/probe outcome feedback (bank wiring, Dev cash, cash-box, Connect status).
 *
 * Channel rule (FORM_SCRIPTS §12.9): each outcome string appears in exactly one place —
 * persistent/ephemeral toned inline here. Do **not** also toastApi the same string.
 * Toast remains allowed only for a *different* message (e.g. thrown genericError with no inline).
 *
 * Tokens: sic-alert + STATUS_TONE_CHIP (--color-an-*) for dark-mode-safe contrast.
 */
import { memo, type ReactElement, type ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { STATUS_TONE_CHIP, type StatusTone } from './statusToneClasses.js';

export type VerifyOutcomeTone = 'success' | 'error' | 'warn';

export interface VerifyOutcomeFeedbackProps {
  readonly tone: VerifyOutcomeTone;
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

function statusToneFor(tone: VerifyOutcomeTone): StatusTone {
  if (tone === 'success') {
    return 'success';
  }
  if (tone === 'error') {
    return 'danger';
  }
  return 'warn';
}

function sicModifier(tone: VerifyOutcomeTone): string {
  if (tone === 'success') {
    return 'sic-alert--success';
  }
  if (tone === 'error') {
    return 'sic-alert--error';
  }
  return 'sic-alert--warn';
}

function OutcomeIcon({ tone }: { readonly tone: VerifyOutcomeTone }): ReactElement {
  const iconProps = {
    className: 'verify-outcome-feedback__icon',
    'aria-hidden': true as const,
    strokeWidth: 2,
    size: 18,
  };
  if (tone === 'success') {
    return <CheckCircle2 {...iconProps} />;
  }
  if (tone === 'error') {
    return <AlertCircle {...iconProps} />;
  }
  return <AlertTriangle {...iconProps} />;
}

export const VerifyOutcomeFeedback = memo(
  ({ tone, children, className, testId }: VerifyOutcomeFeedbackProps): ReactElement => {
    const classes = [
      'sic-alert',
      sicModifier(tone),
      'verify-outcome-feedback',
      STATUS_TONE_CHIP[statusToneFor(tone)],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={classes}
        role={tone === 'error' ? 'alert' : 'status'}
        data-testid={testId}
        data-tone={tone}
      >
        <OutcomeIcon tone={tone} />
        <span className="verify-outcome-feedback__text">{children}</span>
      </div>
    );
  }
);

VerifyOutcomeFeedback.displayName = 'VerifyOutcomeFeedback';
